import type { Result } from '@avalara/types/index';
import type { ListCountriesResult, RegionsResult } from 'types/avatax';

import conf from '../../conf';
import { getContact, getRiskAssessmentPricingData } from '../../reducers/aggregate-logic';
import { selectReturnsPricingData } from '../../selectors/returns';
import { receiveConnectorSalesTax } from '../../store/buy-flow';
import { setCountriesData, setRegionsData } from '../../store/data';
import { setReturnsSalesTax } from '../../store/returns';
import { setRiskAssessmentSalesTax } from '../../store/risk-assessment';
import fetch from '../fetch';
import { getSystemErrorMessaging } from './error';
import { basePostParams } from './index';

import type { Dispatch } from '@avalara/types/actions';
import type { State } from '@avalara/types/reducer-states';
import type {
  CalculateSalesTaxRequest,
  CreateTransactionResponse,
  ResolveAddressRequest,
  ResolveAddressResponse,
  UnauthenticatedUser,
} from '../../../../types';

export const getSalesTax = async (body: CalculateSalesTaxRequest, dispatch: Dispatch) => {
  const postParams = {
    ...basePostParams,
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const result: Result<CreateTransactionResponse> = await fetch(dispatch, {})(
    `${conf.API_URL}/api/avatax/salestax`,
    postParams,
  );

  return result;
};

export const getReturnsSalesTax = () => async (dispatch: Dispatch, getState: () => State) => {
  const state = getState();
  const { subtotal: amount } = selectReturnsPricingData(state);

  if (amount === 0) {
    // Don't call avatax if the sale price is 0
    dispatch(setReturnsSalesTax(0));
    return;
  }

  const items = [
    {
      id: 'avaReturns',
      amount,
      taxCode: 'SP140000',
    },
  ];

  const contact = getContact(state);
  if (contact == null || contact === 'ERROR') {
    dispatch(setReturnsSalesTax(contact));
    return;
  }

  const result = await getSalesTax({ items, contact }, dispatch);

  if (result.ok && result.value.unauthenticatedUser !== true) {
    const salestax = result.value.lines.map((line) => line.taxCalculated)[0];
    dispatch(setReturnsSalesTax(salestax ?? null));
  }
};

export const getRiskAssessmentSalestax = () => async (dispatch: Dispatch, getState: () => State) => {
  const state = getState();
  const { subTotal } = getRiskAssessmentPricingData(state);

  if (subTotal === 0) {
    dispatch(setRiskAssessmentSalesTax(0));
    return;
  }

  const items = [
    {
      id: 'riskAssessment',
      amount: subTotal,
      taxCode: 'SP140000',
    },
  ];

  const contact = getContact(state);
  if (contact == null || contact === 'ERROR') {
    dispatch(setRiskAssessmentSalesTax(contact));
    return;
  }

  const result = await getSalesTax({ items, contact }, dispatch);

  if (result.ok && result.value.unauthenticatedUser !== true) {
    const salestax = result.value.lines.map((line) => line.taxCalculated)[0];
    dispatch(setRiskAssessmentSalesTax(salestax ?? null));
  }
};

export const getConnectorSalesTax = () => async (dispatch: Dispatch, getState: () => State) => {
  const { account, avataxSignup, sharedSignup, system } = getState();
  const { signupForm } = sharedSignup;
  const { activeFlow } = system;
  const { selectedConnector } = avataxSignup;
  const isAvataxDirectBuy = activeFlow === 'avatax-direct-buy';
  const metadata = isAvataxDirectBuy ? selectedConnector?.metadata : account.connector.metadata;
  const connectorId = isAvataxDirectBuy ? selectedConnector?.id : account.connector.id;

  const items = metadata
    ? Object.values(metadata).map((value) => ({
        id: `${connectorId}-tier${value.tierNumber}`,
        amount: value.recurringPrice + (value.waived ? 0 : value.activationPrice),
        taxCode: 'SW052000',
      }))
    : [];

  let contact: ReturnType<typeof getContact>;
  if (isAvataxDirectBuy) {
    contact = {
      street: [signupForm.street1, signupForm.street2].filter(Boolean).join(' '),
      state: signupForm.state,
      postalCode: signupForm.postalCode,
      city: signupForm.city,
      country: 'US',
    };
  } else {
    contact = getContact(getState());
    if (!contact) return;
  }

  const postParams = {
    ...basePostParams,
    body: JSON.stringify({
      avataxAccountId: account.accountId,
      items,
      contact,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const result: Result<CreateTransactionResponse & UnauthenticatedUser> = await fetch(dispatch, {})(
    `${conf.API_URL}/api/avatax/salestax`,
    postParams,
  );

  if (result.ok && result.value.unauthenticatedUser !== true) {
    dispatch(receiveConnectorSalesTax(result.value.lines));
  } else if (!result.ok && result.message === 'no billing contact found') {
    dispatch(getSystemErrorMessaging({ errorCode: 'noBillingContact' }));
  }
};

export const getCountries = () => async (dispatch: Dispatch) => {
  try {
    const result: Result<ListCountriesResult> = await fetch(dispatch)(`${conf.API_URL}/api/avatax/countries`);
    if (!result.ok || !result.value?.value) {
      console.error('Failed to fetch countries: invalid response', {
        ok: result.ok,
        hasValue: result.ok ? !!result.value?.value : false,
      });
      return;
    }
    const sortedCountries = result.value.value.sort(({ name: a }, { name: b }) => (a < b ? -1 : 1));
    dispatch(setCountriesData(sortedCountries));
  } catch (_error) {}
};

export const getRegions = (country: string) => async (dispatch: Dispatch, getState: () => State) => {
  try {
    const {
      data: { regions },
    } = getState();
    if (regions[country] != null) return;
    const result: Result<RegionsResult> = await fetch(dispatch)(
      `${conf.API_URL}/api/avatax/countries/${country}/regions`,
    );
    if (!result.ok || !result.value?.value) {
      console.error('Failed to fetch regions: invalid response', {
        country,
        ok: result.ok,
        hasValue: result.ok ? !!result.value?.value : false,
      });
      dispatch(setRegionsData({ regions: [], country }));
      return;
    }
    const sortedRegions = result.value.value.sort(({ name: a }, { name: b }) => (a < b ? -1 : 1));
    dispatch(setRegionsData({ regions: sortedRegions, country }));
  } catch (_error) {
    dispatch(setRegionsData({ regions: [], country }));
  }
};

export const resolveAddress = async (body: ResolveAddressRequest, dispatch: Dispatch) => {
  const postParams = {
    ...basePostParams,
    body: JSON.stringify(body),
  };

  const result: Result<ResolveAddressResponse> = await fetch(dispatch)(
    `${conf.API_URL}/api/avatax/address/resolve`,
    postParams,
  );

  if (result.ok) return result;
  return result;
};
